import { NavLink, useParams } from "react-router";
import {
  useHtzOrderShowQuery,
  useHtzOrderUpdateStateMutation,
} from "@/services/backend/htz/orders/api/order-api";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { Order } from "@/services/backend/htz/orders/types/order";
import { PersistenceContextProvider } from "@/shared/lib/persistence-state/provider";
import { PersistenceStateUsingContext } from "@/shared/lib/persistence-state/persistence-state-info";
import { DeleteOrderDialog } from "@/routes/processes/[processId]/htz/orders/[orderId]/_components/delete-order-dialog";
import { ErrValidationFailedContextProvider } from "@/shared/lib/errors/err-validation-failed-context";
import t from "@/lang/lang";
import {
  DocumentPage,
  DocumentPageActions,
  DocumentPageContent,
  DocumentPageHeader,
  DocumentPageNavigation,
  DocumentPageNavigationLink,
  DocumentPageNavigationOutlet,
  DocumentPageSkeleton,
} from "@/shared/bsm/core/document/components/ui/document-page";
import { DocumentStateDropdown } from "@/shared/bsm/core/document/components/ui/document-state-dropdown";
import { CpyDialog } from "@/services/backend/cpy/components/cpy-dialog";
import { CpyName } from "@/services/backend/cpy/types/entity-names";
import { NullUUID } from "@/shared/lib/utilities/uuid";
import {
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/shared/components/ui/breadcrumb";
import { ProcessPageMainTemplate } from "@/routes/processes/[processId]/_components/process-page-main-template";
import { HtzBreadcrumbs } from "@/routes/processes/[processId]/htz/_components/htz-breadcrumbs";
import { ReferenceNumber } from "@/services/backend/documents/components/reference-number";

export function OrderRoute() {
  const { orderId } = useParams();

  const {
    data: order,
    isLoading,
    error,
  } = useHtzOrderShowQuery({
    id: orderId!,
  });

  const breadcrumbs = order && (
    <HtzBreadcrumbs customerId={order.customerId} processId={order.processId!}>
      <BreadcrumbSeparator />
      <BreadcrumbItem className="hidden md:block">
        <BreadcrumbLink asChild>
          <NavLink to={`/processes/htz/${order.processId!}/orders`}>
            {t("Auftragseingänge")}
          </NavLink>
        </BreadcrumbLink>
      </BreadcrumbItem>
      <BreadcrumbSeparator />
      <BreadcrumbItem>
        <BreadcrumbPage>
          <ReferenceNumber referenceNumber={order.referenceNumber} />
        </BreadcrumbPage>
      </BreadcrumbItem>
    </HtzBreadcrumbs>
  );

  return (
    <ProcessPageMainTemplate breadcrumbs={breadcrumbs}>
      {error && <RTKQueryErrorAlert error={error} />}
      {isLoading && <DocumentPageSkeleton />}
      {order && <Page order={order} />}
    </ProcessPageMainTemplate>
  );
}

export function Page({ order }: { order: Order }) {
  return (
    <PersistenceContextProvider>
      <ErrValidationFailedContextProvider>
        <DocumentPage>
          <DocumentPageHeader>
            <TabNavigation />
            <DocumentPageActions>
              <PersistenceStateUsingContext className="mr-2" />
              <CpyDialog
                destinationEntityName={CpyName.HTZOrder}
                destinationId={order.id}
                initialParams={{
                  // We limit the order to offers
                  // from the same process. This
                  // is the 90% default what users
                  // need. If needed, they can change
                  // the filter.
                  process: {
                    active: order.processId !== null,
                    values: [order.processId || NullUUID],
                  },
                  orderBy: [{ field: "updatedAt", dsc: true }],
                }}
              />
              <DeleteOrderDialog orderId={order.id} />
              <DocumentStateDropdown
                document={order}
                mutation={useHtzOrderUpdateStateMutation}
              />
            </DocumentPageActions>
          </DocumentPageHeader>
          <DocumentPageContent>
            <DocumentPageNavigationOutlet />
          </DocumentPageContent>
        </DocumentPage>
      </ErrValidationFailedContextProvider>
    </PersistenceContextProvider>
  );
}

function TabNavigation() {
  const links = [
    {
      to: "data",
      label: t("Auftragseingang"),
      fieldName: ["referenceNumber", "date"],
    },
    { to: "positions", label: t("Leistungen"), fieldName: ["positions"] },
    {
      to: "addresses-and-contacts",
      label: t("Adressen & Kontakte"),
      fieldName: [
        "postalAddress",
        "serviceAddress",
        "contact",
        "representative",
      ],
    },
    { to: "files", label: t("Dateien") },
  ];

  return (
    <DocumentPageNavigation>
      {links.map((link) => (
        <DocumentPageNavigationLink
          key={link.to}
          to={link.to}
          fieldName={link.fieldName}
        >
          {link.label}
        </DocumentPageNavigationLink>
      ))}
    </DocumentPageNavigation>
  );
}
